import React, { createContext, FC, ReactNode } from 'react';
import { GetAllLanguagesSuccessResponse } from '@services/useGetAllLanguages';
import { GetAllSettingsSuccessResponse } from '@services/useGetAllSettings';
import { LoginSuccessResponse } from '@services/useLogin';
import { GetUserSuccessResponse } from '@services/useGetUser';
import { google2FAQrCodeSuccessResponse } from '@services/useGoogle2FAQrCode';
import { useEndpointState } from '@hooks/useEndpointState';
import { UserLocation } from '@types';
import { GetSpacesSuccessResponse } from '@services/useGetSpaces';
import { GetSpaceDetailSuccessResponse } from '@services/useGetSpaceDetail';

type EndpointsConfig = {
    [key: string]: {
        storeOnLocalStorage: boolean;
        ttl?: number | null;
        clearOnNewSession?: boolean;
        clearOnLanguageChange?: boolean;
    };
};

const ENDPOINTS: EndpointsConfig = {
    login: {
        storeOnLocalStorage: true,
        ttl: 3600000, // 1 hour - aligned with server token expiry
        clearOnNewSession: true,
    },
    allLanguages: {
        storeOnLocalStorage: true,
        ttl: 600000,
        clearOnNewSession: true,
    },

    allSettings: {
        storeOnLocalStorage: true,
        ttl: 120000, // 2 minutes
        clearOnNewSession: true,
        clearOnLanguageChange: true,
    },
    user: {
        storeOnLocalStorage: false,
        clearOnNewSession: true,
        ttl: null,
    },
    qrCode: {
        storeOnLocalStorage: false,
        clearOnNewSession: true,
        ttl: null,
    },
    spaces: {
        storeOnLocalStorage: false,
        ttl: null,
        clearOnLanguageChange: true,
    },
    spaceDetail: {
        storeOnLocalStorage: false,
        ttl: null,
        clearOnLanguageChange: true,
    },
    lastUserLocationLogged: {
        storeOnLocalStorage: false,
        ttl: null,
    },
};

export type DataContextProps = {
    login: LoginSuccessResponse | null;
    setLogin: (data: LoginSuccessResponse | null) => void;

    allLanguages: GetAllLanguagesSuccessResponse | null;
    setAllLanguages: (data: GetAllLanguagesSuccessResponse) => void;

    allSettings: GetAllSettingsSuccessResponse | null;
    setAllSettings: (data: GetAllSettingsSuccessResponse) => void;

    spaces: GetSpacesSuccessResponse | null;
    setSpaces: (data: GetSpacesSuccessResponse | null) => void;
    isLoadingSpaces: boolean;
    setIsLoadingSpaces: (value: boolean) => void;

    spaceDetail: GetSpaceDetailSuccessResponse | null;
    setSpaceDetail: (data: GetSpaceDetailSuccessResponse | null) => void;
    isLoadingSpaceDetail: boolean;
    setIsLoadingSpaceDetail: (value: boolean) => void;

    user: GetUserSuccessResponse | null;
    setUser: (data: GetUserSuccessResponse | null) => void;
    isLoadingUser: boolean;
    setIsLoadingUser: (value: boolean) => void;

    qrCode: google2FAQrCodeSuccessResponse | null;
    setQrCode: (data: google2FAQrCodeSuccessResponse | null) => void;

    lastUserLocationLogged: UserLocation | null;
    setLastUserLocationLogged: (data: UserLocation | null) => void;
};

export const DataContext = createContext<DataContextProps>({
    login: null,
    setLogin: () => {},
    allLanguages: null,
    setAllLanguages: () => {},

    allSettings: null,
    setAllSettings: () => {},

    user: null,
    setUser: () => {},
    isLoadingUser: false,
    setIsLoadingUser: () => {},

    qrCode: null,
    setQrCode: () => {},

    spaces: null,
    setSpaces: () => {},
    isLoadingSpaces: false,
    setIsLoadingSpaces: () => {},

    spaceDetail: null,
    setSpaceDetail: () => {},
    isLoadingSpaceDetail: false,
    setIsLoadingSpaceDetail: () => {},

    lastUserLocationLogged: null,
    setLastUserLocationLogged: () => {},
});

type DataContextProvider = {
    children: ReactNode;
};

export const DataProvider: FC<DataContextProvider> = ({ children }) => {
    const { setEndpointState, getEndpointState } = useEndpointState(ENDPOINTS);

    const { data: login } = getEndpointState('login');
    getEndpointState('allCategoriesWithProducts');
    getEndpointState('allCategoriesWithServices');
    const { data: allLanguages } = getEndpointState('allLanguages');
    const { data: allSettings } = getEndpointState('allSettings');
    getEndpointState('createdStore');
    const { data: user, loading: isLoadingUser } = getEndpointState('user');
    const { data: qrCode } = getEndpointState('qrCode');
    const { data: spaces, loading: isLoadingSpaces } = getEndpointState('spaces');
    const { data: spaceDetail, loading: isLoadingSpaceDetail } = getEndpointState('spaceDetail');

    const { data: lastUserLocationLogged } = getEndpointState('lastUserLocationLogged');

    const contextValue: DataContextProps = {
        login,
        setLogin: (data) => setEndpointState('login', { data }),

        allLanguages,
        setAllLanguages: (data) => setEndpointState('allLanguages', { data }),

        allSettings,
        setAllSettings: (data) => setEndpointState('allSettings', { data }),

        user,
        isLoadingUser,
        setUser: (data) => setEndpointState('user', { data }),
        setIsLoadingUser: (loading) => setEndpointState('user', { loading }),

        qrCode,
        setQrCode: (data) => setEndpointState('qrCode', { data }),

        spaces,
        isLoadingSpaces,
        setSpaces: (data) => setEndpointState('spaces', { data }),
        setIsLoadingSpaces: (loading) => setEndpointState('spaces', { loading }),

        spaceDetail,
        isLoadingSpaceDetail,
        setSpaceDetail: (data) => setEndpointState('spaceDetail', { data }),
        setIsLoadingSpaceDetail: (loading) => setEndpointState('spaceDetail', { loading }),

        lastUserLocationLogged,
        setLastUserLocationLogged: (data) => setEndpointState('lastUserLocationLogged', { data }),
    };

    return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};
